import { Skeleton } from '@/components/atoms';
import { OkDialog } from '@/components/molecules/dialogs/OkDialog';
import { TextHeader } from '@/components/molecules/header/TextHeader';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useEffect, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';
import { usePaymentCheckout } from '../hooks/usePaymentCheckout';

export default function PaymentCheckoutScreen() {
    useCatalogDynatraceView(
        DT_SCREEN.PAYMENT_CHECKOUT,
        'payment_checkout_viewed',
        true,
    );
    const colors = useUIColors();
    const { t } = useTranslation();
    const insets = useSafeAreaInsets();
    
    // Calculate header height: safe area top + 8px padding + 64px min height
    const headerHeight = insets.top + 8 + 64;

    const {
        checkoutUrl,
        orderRef,
        isLoading,
        error,
        webViewRef,
        handleBackPress,
        handleShouldStartLoadWithRequest,
        handleLoadStart,
        handleLoadEnd,
        handleError: checkoutHandleError,
        handleNavigationStateChange,
        handleMessage,
    } = usePaymentCheckout();

    const handleError: typeof checkoutHandleError = (event) => {
        trackCatalogEvent({
            screen: DT_SCREEN.PAYMENT_CHECKOUT,
            actionName: 'payment_checkout_webview_error',
            eventType: 'error',
        });
        checkoutHandleError(event);
    };

    const [errorDialogVisible, setErrorDialogVisible] = useState(false);

    // Show error dialog when error is set
    useEffect(() => {
        if (error) {
            setErrorDialogVisible(true);
        }
    }, [error]);

    // On web, redirect entire page to payment URL (payment gateways block iframes)
    useEffect(() => {
        if (Platform.OS === 'web' && checkoutUrl) {
            // Store orderRef in localStorage before redirecting (will be lost on full page redirect)
            if (typeof window !== 'undefined' && orderRef) {
                try {
                    localStorage.setItem('payment.orderRef', orderRef);
                } catch (error) {
                    // Silently fail if localStorage is not available
                }
            }
            
            // Small delay to show loading state, then redirect
            const timer = setTimeout(() => {
                if (typeof window !== 'undefined') {
                    window.location.href = checkoutUrl;
                }
            }, 500);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [checkoutUrl, orderRef]);

    // Show error dialog if checkout URL is missing
    if (error) {
        return (
            <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
                <TextHeader
                    title={t('payments.paymentCheckout')}
                    onBackPress={handleBackPress}
                    showSupportButton={false}
                />
                <OkDialog
                    visible={errorDialogVisible}
                    message={error.message}
                    onOk={() => {
                        setErrorDialogVisible(false);
                        handleBackPress();
                    }}
                />
            </View>
        );
    }

    if (!checkoutUrl) {
        return null;
    }

    // On web, show loading message before redirect
    if (Platform.OS === 'web') {
        return (
            <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
                <TextHeader
                    title={t('payments.paymentCheckout')}
                    onBackPress={handleBackPress}
                    showSupportButton={false}
                />
                <ScrollView 
                    style={styles.skeletonScrollView}
                    contentContainerStyle={styles.skeletonContent}
                    showsVerticalScrollIndicator={false}
                >
                    {/* Payment banner skeleton - Telr banner with grey background */}
                    <View style={[styles.paymentBannerSkeleton, { backgroundColor: colors.backgroundLevel3 }]}>
                        <Skeleton width="65%" height={22} borderRadius={4} />
                        <Skeleton width="45%" height={16} borderRadius={4} style={{ marginTop: 12 }} />
                        <Skeleton width="55%" height={20} borderRadius={4} style={{ marginTop: 8 }} />
                    </View>

                    {/* Credit/Debit Card section skeleton */}
                    <View style={styles.sectionSkeleton}>
                        <View style={styles.sectionHeader}>
                            <Skeleton width="45%" height={20} borderRadius={4} />
                            <View style={styles.cardLogosSkeleton}>
                                <Skeleton width={50} height={30} borderRadius={4} />
                                <Skeleton width={50} height={30} borderRadius={4} style={{ marginLeft: 12 }} />
                            </View>
                        </View>
                        {/* Card number field */}
                        <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 20 }} />
                        {/* Expiry month, Expiry year, CVV fields */}
                        <View style={styles.rowSkeleton}>
                            <Skeleton width="30%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                            <Skeleton width="30%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                            <Skeleton width="30%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                        </View>
                    </View>

                    {/* Your details section skeleton */}
                    <View style={styles.sectionSkeleton}>
                        <Skeleton width="38%" height={20} borderRadius={4} />
                        <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 20 }} />
                        <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                        <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                        <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                    </View>

                    {/* Action buttons skeleton */}
                    <View style={styles.buttonsSkeleton}>
                        <Skeleton width="100%" height={52} borderRadius={6} />
                        <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                    </View>

                    {/* Footer skeleton */}
                    <View style={[styles.footerSkeleton, { borderTopColor: colors.outlineInactive }]}>
                        <Skeleton width={90} height={36} borderRadius={4} />
                        <Skeleton width="65%" height={14} borderRadius={4} />
                    </View>
                </ScrollView>
            </View>
        );
    }

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
            <TextHeader
                title={t('payments.paymentCheckout')}
                onBackPress={handleBackPress}
                showSupportButton={false}
            />

            {/* Native: Use WebView */}
            <WebView
                    ref={webViewRef}
                    source={{ uri: checkoutUrl }}
                    originWhitelist={['https://', 'http://']}
                    onShouldStartLoadWithRequest={handleShouldStartLoadWithRequest}
                    onNavigationStateChange={handleNavigationStateChange}
                    onMessage={handleMessage}
                    onLoadStart={handleLoadStart}
                    onLoadEnd={handleLoadEnd}
                    onError={handleError}
                    style={[styles.webView, isLoading && styles.webViewHidden]}
                    javaScriptEnabled={true}
                    domStorageEnabled={true}
                    cacheEnabled={false}
                    startInLoadingState={false}
                    scalesPageToFit={true}
                    allowsBackForwardNavigationGestures={true}
                    setSupportMultipleWindows={false}
                    mixedContentMode="compatibility"
                    allowsInlineMediaPlayback={true}
                    mediaPlaybackRequiresUserAction={false}
                    userAgent={Platform.select({
                        ios: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.0 Mobile/15E148 Safari/604.1',
                        android: 'Mozilla/5.0 (Linux; Android 13; Mobile) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/119.0.0.0 Mobile Safari/537.36',
                        default: undefined as unknown as string,
                    })}
                />

            {isLoading && (
                <View style={[styles.loadingContainer, { backgroundColor: colors.backgroundLevel2, top: headerHeight }]}>
                    <ScrollView 
                        style={styles.skeletonScrollView}
                        contentContainerStyle={styles.skeletonContent}
                        showsVerticalScrollIndicator={false}
                    >
                        {/* Payment banner skeleton - Telr banner with grey background */}
                        <View style={[styles.paymentBannerSkeleton, { backgroundColor: colors.backgroundLevel3 }]}>
                            <Skeleton width="65%" height={22} borderRadius={4} />
                            <Skeleton width="45%" height={16} borderRadius={4} style={{ marginTop: 12 }} />
                            <Skeleton width="55%" height={20} borderRadius={4} style={{ marginTop: 8 }} />
                        </View>

                        {/* Credit/Debit Card section skeleton */}
                        <View style={styles.sectionSkeleton}>
                            <View style={styles.sectionHeader}>
                                <Skeleton width="45%" height={20} borderRadius={4} />
                                <View style={styles.cardLogosSkeleton}>
                                    <Skeleton width={50} height={30} borderRadius={4} />
                                    <Skeleton width={50} height={30} borderRadius={4} style={{ marginLeft: 12 }} />
                                </View>
                            </View>
                            {/* Card number field */}
                            <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 20 }} />
                            {/* Expiry month, Expiry year, CVV fields */}
                            <View style={styles.rowSkeleton}>
                                <Skeleton width="30%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                                <Skeleton width="30%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                                <Skeleton width="30%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                            </View>
                        </View>

                        {/* Your details section skeleton */}
                        <View style={styles.sectionSkeleton}>
                            <Skeleton width="38%" height={20} borderRadius={4} />
                            <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 20 }} />
                            <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                            <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                            <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                        </View>

                        {/* Action buttons skeleton */}
                        <View style={styles.buttonsSkeleton}>
                            <Skeleton width="100%" height={52} borderRadius={6} />
                            <Skeleton width="100%" height={52} borderRadius={6} style={{ marginTop: 16 }} />
                        </View>

                        {/* Footer skeleton */}
                        <View style={[styles.footerSkeleton, { borderTopColor: colors.outlineInactive }]}>
                            <Skeleton width={90} height={36} borderRadius={4} />
                            <Skeleton width="65%" height={14} borderRadius={4} />
                        </View>
                    </ScrollView>
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    webView: {
        flex: 1,
    },
    webViewHidden: {
        opacity: 0,
        pointerEvents: 'none',
    },
    loadingContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10,
    },
    skeletonScrollView: {
        flex: 1,
    },
    skeletonContent: {
        paddingBottom: 20,
    },
    paymentBannerSkeleton: {
        padding: 20,
        marginBottom: 20,
        borderRadius: 8,
    },
    sectionSkeleton: {
        paddingHorizontal: 16,
        marginBottom: 24,
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    cardLogosSkeleton: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    rowSkeleton: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    buttonsSkeleton: {
        paddingHorizontal: 16,
        marginBottom: 24,
    },
    footerSkeleton: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderTopWidth: 1,
    },
});

